// File: src/components/home/ExploreBar.jsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import "../../styles/home/ExploreBar.css";

const dropdownData = {
  "Price Range": [
    "Above ₹15 Lakh",
    "Under ₹3 Lakh",
    "₹12-15 Lakh",
    "₹3-5 Lakh",
    "₹5-8 Lakh",
    "₹8-12 Lakh",
  ],
  "Make and Model": [
    "Honda",
    "Hyundai",
    "Kia",
    "Mahindra",
    "Maruti Suzuki",
    "Nissan",
    "Skoda",
    "Tata",
    "Toyota",
    "Volkswagen",
  ],
  Year: [
    "2009 & above",
    "2011 & above",
    "2013 & above",
    "2015 & above",
    "2017 & above",
    "2019 & above",
    "2021 & above",
    "2023 & above",
  ],
  Fuel: ["CNG", "Diesel", "Electric", "Hybrid", "Petrol"],
  "KM Driven": [
    "1,00,000 kms or less",
    "1,25,000 kms or less",
    "1,50,000 kms or less",
    "1,75,000 kms or less",
    "10,000 kms or less",
    "30,000 kms or less",
    "50,000 kms or less",
    "75,000 kms or less",
  ],
  "Body Type": [
    "Convertible",
    "Coupe",
    "Hatchback",
    "MUV",
    "Sedan",
    "SUV",
    "Wagon",
  ],
  Transmission: ["Automatic", "Manual"],
};

const paramKeyMap = {
  "Price Range": "budget",
  "Make and Model": "brand",
  Year: "year",
  Fuel: "fuelType",
  "KM Driven": "kmDriven",
  "Body Type": "bodyType",
  Transmission: "transmission",
};

// ── Portal Dropdown ──────────────────────────────────────────────────────────
const PortalDropdown = ({
  anchorRef,
  options,
  activeVal,
  onSelect,
  onMouseEnter,
  onMouseLeave,
}) => {
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    const updatePos = () => {
      if (anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        setPos({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: Math.max(rect.width, 200),
        });
      }
    };
    updatePos();
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    return () => {
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
    };
  }, [anchorRef]);

  return ReactDOM.createPortal(
    <div
      className="explore-dropdown-portal"
      style={{
        position: "absolute",
        top: pos.top,
        left: pos.left,
        minWidth: pos.width,
        zIndex: 99999,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <ul>
        {options.map((option) => (
          <li
            key={option}
            className={`explore-dropdown-item ${activeVal === option ? "selected" : ""}`}
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(option);
            }}
          >
            {activeVal === option && <span className="check-icon">✓</span>}
            {option}
          </li>
        ))}
      </ul>
    </div>,
    document.body
  );
};

// ── Single filter pill ───────────────────────────────────────────────────────
const FilterPill = ({ label, activeVal, onSelect, onClear }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const closeTimer = useRef(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  }, []);

  const handleOpen = () => {
    cancelClose();
    setOpen(true);
  };

  useEffect(() => () => cancelClose(), []);

  const handleClear = (e) => {
    e.stopPropagation();
    onClear();
  };

  return (
    <div
      className={`explore-filter-item ${open ? "open" : ""} ${activeVal ? "active" : ""}`}
      onMouseEnter={handleOpen}
      onMouseLeave={scheduleClose}
    >
      <button
        ref={btnRef}
        className="explore-filter-btn"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="filter-btn-text">{activeVal || label}</span>
        {activeVal ? (
          <span className="filter-clear-x" onClick={handleClear} title="Clear">
            ✕
          </span>
        ) : (
          <svg
            className={`filter-chevron ${open ? "rotate" : ""}`}
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        )}
      </button>

      {open && (
        <PortalDropdown
          anchorRef={btnRef}
          options={dropdownData[label]}
          activeVal={activeVal}
          onSelect={(val) => {
            cancelClose();
            onSelect(val);
            setOpen(false);
          }}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        />
      )}
    </div>
  );
};

// ── Main ExploreBar ──────────────────────────────────────────────────────────
const ExploreBar = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState({});

  const handleSelect = (label, value) => {
    const updated = { ...selected, [label]: value };
    setSelected(updated);

    const params = new URLSearchParams();
    Object.entries(updated).forEach(([key, val]) => {
      if (val) {
        const paramKey = paramKeyMap[key] || key.toLowerCase();
        params.append(paramKey, val);
      }
    });
    navigate(`/buy-cars?${params.toString()}`);
  };

  const handleClear = (label) => {
    const updated = { ...selected };
    delete updated[label];
    setSelected(updated);
  };

  return (
    <nav className="explore-bar">
      <div className="explore-bar-inner">
        <span className="explore-label">Explore By</span>
        <div className="explore-filters">
          {Object.keys(dropdownData).map((label) => (
            <FilterPill
              key={label}
              label={label}
              activeVal={selected[label] || ""}
              onSelect={(val) => handleSelect(label, val)}
              onClear={() => handleClear(label)}
            />
          ))}
        </div>
      </div>
    </nav>
  );
};

export default ExploreBar;